import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Shield,
  FileText,
  Scale,
  Phone,
  Mail,
  Globe,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle2,
  Clock,
  Building2,
  Gavel,
  HelpCircle,
  BookOpen,
  Users,
  FileCheck,
  AlertTriangle,
  ArrowRight,
  ExternalLink,
  Heart,
  Briefcase
} from "lucide-react";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

// Accordion Component
function Accordion({ title, children, icon: Icon, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-[#C9D4E6] rounded-xl overflow-hidden mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-[#EDF1F8]/50 hover:bg-[#EDF1F8] transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="h-5 w-5 text-[#A87608]" />}
          <span className="font-semibold text-[#1B2A4A]">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-[#3A4557]" />
        ) : (
          <ChevronDown className="h-5 w-5 text-[#3A4557]" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-white/50 border-t border-[#C9D4E6]">
          {children}
        </div>
      )}
    </div>
  );
}

// Step Component
function Step({ number, title, description }) {
  return (
    <div className="flex gap-4 mb-4">
      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-[#D4A017]/15 border border-[#D4A017]/50 flex items-center justify-center">
        <span className="text-[#A87608] font-bold text-sm">{number}</span>
      </div>
      <div>
        <h4 className="text-[#1B2A4A] font-medium">{title}</h4>
        <p className="text-[#3A4557] text-sm mt-1">{description}</p>
      </div>
    </div>
  );
}

// Info Card Component
function InfoCard({ title, value, icon: Icon, color = "amber" }) {
  const colors = {
    amber: "bg-[#D4A017]/10 border-[#D4A017]/30 text-[#A87608]",
    green: "bg-green-500/10 border-green-500/30 text-green-600",
    blue: "bg-[#EDF1F8]/30 border-blue-500/30 text-[#2E3B57]",
    red: "bg-red-500/10 border-red-500/30 text-red-600"
  };
  
  return (
    <div className={`p-4 rounded-xl border ${colors[color]}`}>
      <div className="flex items-center gap-3">
        {Icon && <Icon className="h-6 w-6" />}
        <div>
          <p className="text-[#3A4557] text-xs">{title}</p>
          <p className="font-bold">{value}</p>
        </div>
      </div>
    </div>
  );
}

export default function PolicyholderRights() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("health");
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8]">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-[#C9D4E6] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="text-[#2E3B57] hover:text-[#1B2A4A]"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
            <h1 className="text-xl font-bold text-[#1B2A4A] flex items-center gap-2">
              <Shield className="h-6 w-6 text-[#A87608]" />
              Policyholder Rights
            </h1>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-12 px-4 sm:px-6 bg-gradient-to-b from-[#D4A017]/10 to-transparent">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mb-4">
              Know Your <span className="text-[#A87608]">Rights</span> as a Policyholder
            </h1>
            <p className="text-[#3A4557] text-lg max-w-2xl mx-auto">
              Understanding your rights is the first step to ensuring fair treatment. 
              Learn about IRDAI guidelines, claim processes, and how to fight for your rightful claims.
            </p>
          </motion.div>
          
          {/* Quick Stats */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8"
          >
            <InfoCard title="Moratorium Period" value="5 Years" icon={Clock} color="green" />
            <InfoCard title="Claim TAT (Cashless)" value="1 Hour" icon={FileCheck} color="blue" />
            <InfoCard title="Grievance Resolution" value="15 Days" icon={Scale} color="amber" />
            <InfoCard title="Ombudsman Complaint" value="1 Year Limit" icon={Gavel} color="red" />
          </motion.div>
        </div>
      </section>

      {/* Tab Selector */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex gap-2 p-1 bg-[#EDF1F8]/50 rounded-xl">
          <button
            onClick={() => setActiveTab("health")}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === "health"
                ? "bg-green-500 text-white"
                : "text-[#3A4557] hover:text-[#1B2A4A] hover:bg-[#E2E9F4]"
            }`}
          >
            <Heart className="h-5 w-5" />
            Health Insurance
          </button>
          <button
            onClick={() => setActiveTab("life")}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === "life"
                ? "bg-blue-500 text-white"
                : "text-[#3A4557] hover:text-[#1B2A4A] hover:bg-[#E2E9F4]"
            }`}
          >
            <Briefcase className="h-5 w-5" />
            Life / Term Insurance
          </button>
        </div>
      </div>

      {/* Main Content */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-4xl mx-auto px-4 sm:px-6 pb-12"
      >
        {activeTab === "health" ? (
          // Health Insurance Content
          <>
            {/* Moratorium Period - Most Important */}
            <motion.div variants={itemVariants} className="mb-8">
              <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/10 border border-green-500/30 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-green-600 mb-2">
                      🛡️ The 5-Year Moratorium Rule - Your Biggest Protection
                    </h2>
                    <p className="text-[#2E3B57] mb-4">
                      After <strong className="text-[#1B2A4A]">5 continuous years</strong> of policy renewal (reduced from 8 years as per IRDAI guidelines effective April 2024), 
                      your health insurance policy <strong className="text-green-600">CANNOT be cancelled or claim rejected</strong> on 
                      grounds of non-disclosure or misrepresentation, except in cases of proven fraud.
                    </p>
                    <div className="bg-white/50 rounded-xl p-4 mt-4">
                      <h4 className="text-[#1B2A4A] font-semibold mb-2">What this means:</h4>
                      <ul className="space-y-2 text-[#3A4557]">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>Even if you forgot to disclose a pre-existing condition, the claim cannot be denied after 5 years</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>Policy renewal cannot be refused citing non-disclosure</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>This is as per IRDAI (Health Insurance) Regulations, updated in April 2024</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Claim Process */}
            <motion.div variants={itemVariants}>
              <h2 className="text-2xl font-bold text-[#1B2A4A] mb-6 flex items-center gap-3">
                <FileText className="h-7 w-7 text-[#A87608]" />
                Claim Process & Your Rights
              </h2>
              
              <Accordion title="Cashless Claim Process" icon={FileCheck} defaultOpen={true}>
                <div className="space-y-4">
                  <p className="text-[#2E3B57]">
                    In a cashless claim, the insurance company directly settles the bill with the hospital. 
                    Here's what you need to know:
                  </p>
                  
                  <div className="bg-[#EDF1F8]/30 border border-blue-500/30 rounded-xl p-4">
                    <h4 className="text-[#2E3B57] font-semibold mb-3">⏱️ IRDAI Mandated Timelines:</h4>
                    <ul className="space-y-2 text-[#2E3B57]">
                      <li>• <strong>Pre-authorization:</strong> Must be approved/rejected within <strong className="text-[#1B2A4A]">1 hour</strong> for emergencies</li>
                      <li>• <strong>Planned hospitalization:</strong> Decision within <strong className="text-[#1B2A4A]">24 hours</strong></li>
                      <li>• <strong>Final settlement:</strong> Within <strong className="text-[#1B2A4A]">3 hours</strong> of discharge</li>
                    </ul>
                  </div>
                  
                  <h4 className="text-[#1B2A4A] font-semibold mt-4">Steps for Cashless Claim:</h4>
                  <Step number="1" title="Inform TPA/Insurer" description="Notify within 24 hours of admission (or before for planned hospitalization)" />
                  <Step number="2" title="Submit Pre-auth Form" description="Hospital submits pre-authorization request with estimated costs" />
                  <Step number="3" title="Get Approval" description="Insurer approves the cashless request (may be partial or full)" />
                  <Step number="4" title="Treatment & Discharge" description="Show health card, get treatment, sign discharge summary" />
                  <Step number="5" title="Settlement" description="Insurer settles directly with hospital; you pay only non-covered items" />
                </div>
              </Accordion>

              <Accordion title="Reimbursement Claim Process" icon={FileText}>
                <div className="space-y-4">
                  <p className="text-[#2E3B57]">
                    When cashless is not available or you choose to pay first and claim later:
                  </p>
                  
                  <div className="bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl p-4">
                    <h4 className="text-[#A87608] font-semibold mb-3">⏱️ IRDAI Mandated Timelines:</h4>
                    <ul className="space-y-2 text-[#2E3B57]">
                      <li>• <strong>Claim submission:</strong> Within <strong className="text-[#1B2A4A]">15-30 days</strong> of discharge (check your policy)</li>
                      <li>• <strong>Insurer acknowledgment:</strong> Within <strong className="text-[#1B2A4A]">24 hours</strong></li>
                      <li>• <strong>Document request:</strong> Within <strong className="text-[#1B2A4A]">15 days</strong> of claim receipt</li>
                      <li>• <strong>Claim settlement:</strong> Within <strong className="text-[#1B2A4A]">30 days</strong> of receiving all documents</li>
                    </ul>
                  </div>
                  
                  <h4 className="text-[#1B2A4A] font-semibold mt-4">Required Documents:</h4>
                  <ul className="grid grid-cols-2 gap-2 text-[#3A4557] mt-2">
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> Claim form (signed)</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> Original hospital bills</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> Discharge summary</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> Investigation reports</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> Pharmacy bills</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> Doctor's prescription</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> KYC documents</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> Policy copy</li>
                  </ul>
                </div>
              </Accordion>
            </motion.div>

            {/* Claim Rejection & Fighting */}
            <motion.div variants={itemVariants} className="mt-8">
              <h2 className="text-2xl font-bold text-[#1B2A4A] mb-6 flex items-center gap-3">
                <AlertTriangle className="h-7 w-7 text-red-600" />
                If Your Claim is Rejected
              </h2>
              
              <Accordion title="Common Reasons for Claim Rejection" icon={AlertCircle}>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-red-500/10 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#1B2A4A] font-medium">Non-disclosure of Pre-existing Disease (PED)</p>
                      <p className="text-[#3A4557] text-sm">Not declaring medical history at policy purchase</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-red-500/10 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#1B2A4A] font-medium">Waiting Period Not Completed</p>
                      <p className="text-[#3A4557] text-sm">Claiming before initial/specific disease waiting period ends</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-red-500/10 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#1B2A4A] font-medium">Policy Exclusions</p>
                      <p className="text-[#3A4557] text-sm">Treatment falls under permanent or specific exclusions</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-red-500/10 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#1B2A4A] font-medium">Late Intimation</p>
                      <p className="text-[#3A4557] text-sm">Not informing insurer within specified time</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-red-500/10 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#1B2A4A] font-medium">Incomplete Documentation</p>
                      <p className="text-[#3A4557] text-sm">Missing or incorrect claim documents</p>
                    </div>
                  </div>
                </div>
              </Accordion>

              <Accordion title="How to Fight a Rejected Claim" icon={Scale} defaultOpen={true}>
                <div className="space-y-4">
                  <div className="bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl p-4 mb-4">
                    <p className="text-[#A87608] font-semibold">💡 Important: Don't give up!</p>
                    <p className="text-[#2E3B57] mt-1">
                      Many claims are wrongly rejected. You have multiple levels of appeal available.
                    </p>
                  </div>

                  <h4 className="text-[#1B2A4A] font-semibold">Step-by-Step Escalation Process:</h4>
                  
                  <div className="space-y-4 mt-4">
                    <div className="bg-[#EDF1F8]/50 rounded-xl p-4 border-l-4 border-blue-500">
                      <h5 className="text-[#2E3B57] font-semibold">Level 1: Insurance Company Grievance Cell</h5>
                      <p className="text-[#3A4557] text-sm mt-1">Timeline: 15 days for resolution</p>
                      <ul className="mt-2 text-[#2E3B57] text-sm space-y-1">
                        <li>• Write to the company's Grievance Redressal Officer (GRO)</li>
                        <li>• Attach all documents and rejection letter</li>
                        <li>• Keep acknowledgment receipt</li>
                      </ul>
                    </div>
                    
                    <div className="flex justify-center">
                      <ArrowRight className="h-6 w-6 text-[#3A4557] rotate-90" />
                    </div>
                    
                    <div className="bg-[#EDF1F8]/50 rounded-xl p-4 border-l-4 border-[#D4A017]">
                      <h5 className="text-[#A87608] font-semibold">Level 2: IGMS Portal (IRDAI)</h5>
                      <p className="text-[#3A4557] text-sm mt-1">If no response in 15 days or unsatisfied</p>
                      <ul className="mt-2 text-[#2E3B57] text-sm space-y-1">
                        <li>• Visit: <a href="https://igms.irda.gov.in" target="_blank" rel="noopener noreferrer" className="text-[#2E3B57] hover:underline">igms.irda.gov.in</a></li>
                        <li>• Register complaint with all details</li>
                        <li>• Track complaint status online</li>
                      </ul>
                    </div>
                    
                    <div className="flex justify-center">
                      <ArrowRight className="h-6 w-6 text-[#3A4557] rotate-90" />
                    </div>
                    
                    <div className="bg-[#EDF1F8]/50 rounded-xl p-4 border-l-4 border-purple-500">
                      <h5 className="text-purple-600 font-semibold">Level 3: Insurance Ombudsman</h5>
                      <p className="text-[#3A4557] text-sm mt-1">For claims up to ₹50 Lakhs</p>
                      <ul className="mt-2 text-[#2E3B57] text-sm space-y-1">
                        <li>• Must file within 1 year of rejection</li>
                        <li>• Free of cost - No lawyer needed</li>
                        <li>• Decision within 3 months</li>
                        <li>• Ombudsman's decision is binding on insurer</li>
                      </ul>
                    </div>
                    
                    <div className="flex justify-center">
                      <ArrowRight className="h-6 w-6 text-[#3A4557] rotate-90" />
                    </div>
                    
                    <div className="bg-[#EDF1F8]/50 rounded-xl p-4 border-l-4 border-red-500">
                      <h5 className="text-red-600 font-semibold">Level 4: Consumer Forum / Court</h5>
                      <p className="text-[#3A4557] text-sm mt-1">Last resort for legal action</p>
                      <ul className="mt-2 text-[#2E3B57] text-sm space-y-1">
                        <li>• District Forum: Claims up to ₹1 Crore</li>
                        <li>• State Commission: ₹1-10 Crore</li>
                        <li>• National Commission: Above ₹10 Crore</li>
                        <li>• Can claim compensation + mental harassment damages</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Accordion>
            </motion.div>

            {/* Insurance Ombudsman */}
            <motion.div variants={itemVariants} className="mt-8">
              <h2 className="text-2xl font-bold text-[#1B2A4A] mb-6 flex items-center gap-3">
                <Gavel className="h-7 w-7 text-purple-600" />
                Insurance Ombudsman
              </h2>
              
              <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/10 border border-purple-500/30 rounded-2xl p-6 mb-6">
                <h3 className="text-purple-600 font-bold text-lg mb-3">What is Insurance Ombudsman?</h3>
                <p className="text-[#2E3B57]">
                  The Insurance Ombudsman is a <strong className="text-[#1B2A4A]">FREE grievance redressal mechanism</strong> established 
                  by the Government of India to resolve complaints against insurance companies quickly and fairly.
                </p>
              </div>

              <Accordion title="Who Can Approach the Ombudsman?" icon={Users}>
                <ul className="space-y-2 text-[#2E3B57]">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Any individual policyholder or nominee/legal heir</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Claim value must be up to <strong className="text-[#1B2A4A]">₹50 Lakhs</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Must have first approached the insurance company</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Complaint within 1 year of insurer's final response</span>
                  </li>
                </ul>
              </Accordion>

              <Accordion title="How to File Complaint with Ombudsman" icon={FileText}>
                <Step number="1" title="Identify Your Ombudsman" description="Find the ombudsman office based on your location or insurer's branch location" />
                <Step number="2" title="Prepare Documents" description="Complaint letter, policy copy, claim documents, rejection letter, correspondence with insurer" />
                <Step number="3" title="Submit Complaint" description="Send by post/email or submit online at cioins.co.in" />
                <Step number="4" title="Attend Hearing" description="Ombudsman may call for personal hearing (in-person or video)" />
                <Step number="5" title="Receive Award" description="Decision within 3 months; binding on insurer within 30 days" />
                
                <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                  <p className="text-green-600 font-semibold">🎯 Key Advantage:</p>
                  <p className="text-[#2E3B57] mt-1">
                    The Ombudsman's award is <strong className="text-[#1B2A4A]">binding on the insurer</strong> but 
                    you can still go to court if unsatisfied!
                  </p>
                </div>
              </Accordion>

              <Accordion title="Ombudsman Contact Details" icon={Phone}>
                <div className="space-y-4">
                  <div className="bg-[#EDF1F8]/50 rounded-xl p-4">
                    <p className="text-[#1B2A4A] font-semibold mb-2">📍 Find Your Ombudsman Office:</p>
                    <a 
                      href="https://cioins.co.in/ombudsman-locator" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-[#2E3B57] hover:underline"
                    >
                      <Globe className="h-4 w-4" />
                      cioins.co.in/ombudsman-locator
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  
                  <div className="bg-[#EDF1F8]/50 rounded-xl p-4">
                    <p className="text-[#1B2A4A] font-semibold mb-2">📧 Online Complaint Portal:</p>
                    <a 
                      href="https://cioins.co.in" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-[#2E3B57] hover:underline"
                    >
                      <Globe className="h-4 w-4" />
                      cioins.co.in (Council for Insurance Ombudsmen)
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  
                  <div className="bg-[#EDF1F8]/50 rounded-xl p-4">
                    <p className="text-[#1B2A4A] font-semibold mb-2">📞 IRDAI Helpline:</p>
                    <p className="text-[#2E3B57]">Toll Free: <span className="text-green-600 font-bold">155255</span></p>
                    <p className="text-[#2E3B57]">Email: <span className="text-[#2E3B57]">complaints@irdai.gov.in</span></p>
                  </div>
                </div>
              </Accordion>
            </motion.div>

            {/* Your Rights Summary */}
            <motion.div variants={itemVariants} className="mt-8">
              <h2 className="text-2xl font-bold text-[#1B2A4A] mb-6 flex items-center gap-3">
                <BookOpen className="h-7 w-7 text-green-600" />
                Your Key Rights as a Health Insurance Policyholder
              </h2>
              
              <div className="grid gap-4">
                <div className="bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl p-4 flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-[#1B2A4A] font-semibold">Right to Free Look Period</h4>
                    <p className="text-[#3A4557] text-sm">15-30 days to review and return policy if not satisfied</p>
                  </div>
                </div>
                
                <div className="bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl p-4 flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-[#1B2A4A] font-semibold">Right to Lifelong Renewal</h4>
                    <p className="text-[#3A4557] text-sm">Insurer cannot refuse renewal except for fraud or non-payment</p>
                  </div>
                </div>
                
                <div className="bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl p-4 flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-[#1B2A4A] font-semibold">Right to Port Your Policy</h4>
                    <p className="text-[#3A4557] text-sm">Transfer to another insurer without losing waiting period credits</p>
                  </div>
                </div>
                
                <div className="bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl p-4 flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-[#1B2A4A] font-semibold">Right to Grace Period</h4>
                    <p className="text-[#3A4557] text-sm">15-30 days after due date to pay premium without losing coverage</p>
                  </div>
                </div>
                
                <div className="bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl p-4 flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-[#1B2A4A] font-semibold">Right to Claim Settlement Reasons</h4>
                    <p className="text-[#3A4557] text-sm">Insurer must provide detailed written reasons for any rejection</p>
                  </div>
                </div>
                
                <div className="bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl p-4 flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-[#1B2A4A] font-semibold">Right to 5-Year Moratorium</h4>
                    <p className="text-[#3A4557] text-sm">No rejection for non-disclosure after 5 years of continuous coverage (updated April 2024)</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        ) : (
          // Life Insurance Content
          <>
            {/* Moratorium Period for Life Insurance */}
            <motion.div variants={itemVariants} className="mb-8">
              <div className="bg-gradient-to-r from-[#EDF1F8]/20 to-cyan-500/10 border border-blue-500/30 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-full bg-[#EDF1F8]/40 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-[#2E3B57]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#2E3B57] mb-2">
                      🛡️ The 3-Year Contestability Period - Life Insurance Protection
                    </h2>
                    <p className="text-[#2E3B57] mb-4">
                      After <strong className="text-[#1B2A4A]">3 years</strong> from policy issuance, 
                      a life insurance policy <strong className="text-[#2E3B57]">becomes incontestable</strong>. 
                      The insurer cannot challenge the policy validity except in cases of proven fraud.
                    </p>
                    <div className="bg-white/50 rounded-xl p-4 mt-4">
                      <h4 className="text-[#1B2A4A] font-semibold mb-2">Section 45 of Insurance Act, 1938 (Amended):</h4>
                      <ul className="space-y-2 text-[#3A4557]">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-[#2E3B57] flex-shrink-0 mt-0.5" />
                          <span>No policy can be called into question after 3 years</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-[#2E3B57] flex-shrink-0 mt-0.5" />
                          <span>Insurer must prove fraud with material facts within 3 years</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-[#2E3B57] flex-shrink-0 mt-0.5" />
                          <span>After 3 years, only proven fraud can void the policy</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Life Insurance Claim Process */}
            <motion.div variants={itemVariants}>
              <h2 className="text-2xl font-bold text-[#1B2A4A] mb-6 flex items-center gap-3">
                <FileText className="h-7 w-7 text-[#A87608]" />
                Life Insurance Claim Process
              </h2>
              
              <Accordion title="Death Claim Process" icon={FileCheck} defaultOpen={true}>
                <div className="space-y-4">
                  <div className="bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl p-4">
                    <h4 className="text-[#A87608] font-semibold mb-3">⏱️ IRDAI Mandated Timelines:</h4>
                    <ul className="space-y-2 text-[#2E3B57]">
                      <li>• <strong>Claim acknowledgment:</strong> Within <strong className="text-[#1B2A4A]">24 hours</strong></li>
                      <li>• <strong>Document requirement:</strong> Within <strong className="text-[#1B2A4A]">15 days</strong></li>
                      <li>• <strong>Claim settlement (simple):</strong> Within <strong className="text-[#1B2A4A]">30 days</strong></li>
                      <li>• <strong>Investigation cases:</strong> Within <strong className="text-[#1B2A4A]">90 days</strong></li>
                      <li>• <strong>Interest on delayed payment:</strong> 2% above bank rate</li>
                    </ul>
                  </div>
                  
                  <h4 className="text-[#1B2A4A] font-semibold mt-4">Steps for Death Claim:</h4>
                  <Step number="1" title="Intimate the Insurer" description="Nominee/legal heir should inform the insurance company immediately" />
                  <Step number="2" title="Collect Required Documents" description="Death certificate, policy documents, identity proof, claimant's bank details" />
                  <Step number="3" title="Submit Claim Form" description="Fill the claim form provided by the insurer with all details" />
                  <Step number="4" title="Investigation (if any)" description="Insurer may investigate for claims within 3 years or suspicious cases" />
                  <Step number="5" title="Settlement" description="Receive claim amount via bank transfer" />
                  
                  <h4 className="text-[#1B2A4A] font-semibold mt-4">Required Documents:</h4>
                  <ul className="grid grid-cols-2 gap-2 text-[#3A4557] mt-2">
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> Original policy document</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> Death certificate</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> Claimant's ID proof</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> Claimant's address proof</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> Cancelled cheque</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> Hospital records (if applicable)</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> FIR/PM report (if unnatural death)</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> Legal heir certificate (if no nominee)</li>
                  </ul>
                </div>
              </Accordion>

              <Accordion title="Maturity/Survival Benefit Claim" icon={FileText}>
                <div className="space-y-4">
                  <p className="text-[#2E3B57]">
                    For endowment policies or policies with survival benefits:
                  </p>
                  <Step number="1" title="Receive Intimation" description="Insurer usually sends advance notice before maturity date" />
                  <Step number="2" title="Submit Documents" description="Original policy, discharge form, KYC documents, bank details" />
                  <Step number="3" title="Receive Payment" description="Maturity amount credited within 15 days of document submission" />
                  
                  <div className="mt-4 p-4 bg-[#EDF1F8]/30 border border-blue-500/30 rounded-xl">
                    <p className="text-[#2E3B57] font-semibold">💡 Tip:</p>
                    <p className="text-[#2E3B57] mt-1">
                      Update your bank details and contact information with the insurer 2-3 months before maturity 
                      to ensure smooth and timely payment.
                    </p>
                  </div>
                </div>
              </Accordion>
            </motion.div>

            {/* Life Insurance Claim Rejection */}
            <motion.div variants={itemVariants} className="mt-8">
              <h2 className="text-2xl font-bold text-[#1B2A4A] mb-6 flex items-center gap-3">
                <AlertTriangle className="h-7 w-7 text-red-600" />
                Life Insurance Claim Rejection & Appeals
              </h2>
              
              <Accordion title="Common Reasons for Life Insurance Claim Rejection" icon={AlertCircle}>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-red-500/10 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#1B2A4A] font-medium">Non-disclosure of Medical History</p>
                      <p className="text-[#3A4557] text-sm">Not declaring existing diseases, surgeries, or treatments</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-red-500/10 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#1B2A4A] font-medium">Suicide within First Year</p>
                      <p className="text-[#3A4557] text-sm">Most policies exclude suicide in the first year</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-red-500/10 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#1B2A4A] font-medium">Policy Lapse</p>
                      <p className="text-[#3A4557] text-sm">Premium not paid and policy not revived before death</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-red-500/10 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#1B2A4A] font-medium">Misstatement of Age</p>
                      <p className="text-[#3A4557] text-sm">Incorrect age declaration affecting risk assessment</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-red-500/10 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#1B2A4A] font-medium">Death due to Excluded Cause</p>
                      <p className="text-[#3A4557] text-sm">Adventure sports, war, nuclear hazards, etc.</p>
                    </div>
                  </div>
                </div>
              </Accordion>

              <Accordion title="How to Fight a Rejected Life Insurance Claim" icon={Scale} defaultOpen={true}>
                <div className="space-y-4">
                  <div className="bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl p-4 mb-4">
                    <p className="text-[#A87608] font-semibold">💪 Remember:</p>
                    <p className="text-[#2E3B57] mt-1">
                      After 3 years, the burden of proving fraud lies with the insurer, not you!
                    </p>
                  </div>

                  <h4 className="text-[#1B2A4A] font-semibold">Escalation Hierarchy (Same as Health Insurance):</h4>
                  
                  <div className="space-y-3 mt-4">
                    <div className="flex items-center gap-3 p-3 bg-[#EDF1F8]/30 rounded-lg">
                      <span className="h-8 w-8 rounded-full bg-[#EDF1F8]/40 flex items-center justify-center text-[#2E3B57] font-bold">1</span>
                      <div>
                        <p className="text-[#1B2A4A] font-medium">Company Grievance Cell → 15 days</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-[#D4A017]/10 rounded-lg">
                      <span className="h-8 w-8 rounded-full bg-[#D4A017]/30 flex items-center justify-center text-[#A87608] font-bold">2</span>
                      <div>
                        <p className="text-[#1B2A4A] font-medium">IGMS Portal (igms.irda.gov.in)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-purple-500/10 rounded-lg">
                      <span className="h-8 w-8 rounded-full bg-purple-500/30 flex items-center justify-center text-purple-600 font-bold">3</span>
                      <div>
                        <p className="text-[#1B2A4A] font-medium">Insurance Ombudsman (up to ₹50 Lakhs)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-red-500/10 rounded-lg">
                      <span className="h-8 w-8 rounded-full bg-red-500/30 flex items-center justify-center text-red-600 font-bold">4</span>
                      <div>
                        <p className="text-[#1B2A4A] font-medium">Consumer Forum / Civil Court</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Accordion>
            </motion.div>

            {/* Life Insurance Rights */}
            <motion.div variants={itemVariants} className="mt-8">
              <h2 className="text-2xl font-bold text-[#1B2A4A] mb-6 flex items-center gap-3">
                <BookOpen className="h-7 w-7 text-[#2E3B57]" />
                Your Key Rights as a Life Insurance Policyholder
              </h2>
              
              <div className="grid gap-4">
                <div className="bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl p-4 flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-[#EDF1F8]/40 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-[#2E3B57]" />
                  </div>
                  <div>
                    <h4 className="text-[#1B2A4A] font-semibold">Right to Free Look Period</h4>
                    <p className="text-[#3A4557] text-sm">15-30 days to review and cancel policy with full refund</p>
                  </div>
                </div>
                
                <div className="bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl p-4 flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-[#EDF1F8]/40 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-[#2E3B57]" />
                  </div>
                  <div>
                    <h4 className="text-[#1B2A4A] font-semibold">Right to Grace Period</h4>
                    <p className="text-[#3A4557] text-sm">30 days for yearly/half-yearly, 15 days for monthly premiums</p>
                  </div>
                </div>
                
                <div className="bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl p-4 flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-[#EDF1F8]/40 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-[#2E3B57]" />
                  </div>
                  <div>
                    <h4 className="text-[#1B2A4A] font-semibold">Right to Revival</h4>
                    <p className="text-[#3A4557] text-sm">Revive lapsed policy within 5 years by paying arrears + interest</p>
                  </div>
                </div>
                
                <div className="bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl p-4 flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-[#EDF1F8]/40 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-[#2E3B57]" />
                  </div>
                  <div>
                    <h4 className="text-[#1B2A4A] font-semibold">Right to Assign/Transfer Policy</h4>
                    <p className="text-[#3A4557] text-sm">Transfer policy ownership or use as collateral for loans</p>
                  </div>
                </div>
                
                <div className="bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl p-4 flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-[#EDF1F8]/40 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-[#2E3B57]" />
                  </div>
                  <div>
                    <h4 className="text-[#1B2A4A] font-semibold">Right to Change Nominee</h4>
                    <p className="text-[#3A4557] text-sm">Update nominee anytime during policy term at no extra cost</p>
                  </div>
                </div>
                
                <div className="bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl p-4 flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-[#EDF1F8]/40 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-[#2E3B57]" />
                  </div>
                  <div>
                    <h4 className="text-[#1B2A4A] font-semibold">Right to Incontestability (After 3 Years)</h4>
                    <p className="text-[#3A4557] text-sm">Policy cannot be challenged for non-disclosure after 3 years</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}

        {/* Important Links Section */}
        <motion.div variants={itemVariants} className="mt-12">
          <h2 className="text-2xl font-bold text-[#1B2A4A] mb-6 flex items-center gap-3">
            <Globe className="h-7 w-7 text-[#2E3B57]" />
            Important Links & Resources
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <a 
              href="https://igms.irda.gov.in" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl p-4 hover:border-blue-500/50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Building2 className="h-8 w-8 text-[#2E3B57]" />
                <div>
                  <h4 className="text-[#1B2A4A] font-semibold group-hover:text-[#2E3B57] transition-colors">IGMS Portal</h4>
                  <p className="text-[#3A4557] text-sm">IRDAI Grievance Management System</p>
                </div>
                <ExternalLink className="h-4 w-4 text-[#3A4557] ml-auto" />
              </div>
            </a>
            
            <a 
              href="https://cioins.co.in" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl p-4 hover:border-purple-500/50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Gavel className="h-8 w-8 text-purple-600" />
                <div>
                  <h4 className="text-[#1B2A4A] font-semibold group-hover:text-purple-600 transition-colors">Insurance Ombudsman</h4>
                  <p className="text-[#3A4557] text-sm">Council for Insurance Ombudsmen</p>
                </div>
                <ExternalLink className="h-4 w-4 text-[#3A4557] ml-auto" />
              </div>
            </a>
            
            <a 
              href="https://irdai.gov.in" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl p-4 hover:border-[#D4A017]/50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Shield className="h-8 w-8 text-[#A87608]" />
                <div>
                  <h4 className="text-[#1B2A4A] font-semibold group-hover:text-[#A87608] transition-colors">IRDAI Official Website</h4>
                  <p className="text-[#3A4557] text-sm">Insurance Regulatory Authority</p>
                </div>
                <ExternalLink className="h-4 w-4 text-[#3A4557] ml-auto" />
              </div>
            </a>
            
            <a 
              href="https://consumerhelpline.gov.in" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl p-4 hover:border-green-500/50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Phone className="h-8 w-8 text-green-600" />
                <div>
                  <h4 className="text-[#1B2A4A] font-semibold group-hover:text-green-600 transition-colors">Consumer Helpline</h4>
                  <p className="text-[#3A4557] text-sm">National Consumer Helpline: 1800-11-4000</p>
                </div>
                <ExternalLink className="h-4 w-4 text-[#3A4557] ml-auto" />
              </div>
            </a>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div variants={itemVariants} className="mt-12">
          <div className="bg-gradient-to-r from-[#D4A017]/20 to-[#D9A82C]/10 border border-[#D4A017]/30 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-[#1B2A4A] mb-3">Need Help with Your Claim?</h3>
            <p className="text-[#2E3B57] mb-6">
              If you're facing issues with your insurance claim, we're here to guide you through the process.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                onClick={() => window.open('https://wa.me/919510020907?text=Hi%20Prashant,%20I%20need%20help%20with%20my%20insurance%20claim.', '_blank')}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                <Phone className="h-4 w-4 mr-2" />
                WhatsApp Us
              </Button>
              <Button
                onClick={() => window.location.href = 'tel:+919510020907'}
                variant="outline"
                className="border-[#D4A017]/50 text-[#A87608] hover:bg-[#D4A017]/10"
              >
                <Phone className="h-4 w-4 mr-2" />
                Call Now
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Footer */}
      <footer className="bg-white/80 border-t border-[#C9D4E6] py-6 px-4 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#3A4557] text-sm">
            Disclaimer: This information is for educational purposes only. For specific legal advice, please consult a qualified professional.
          </p>
          <p className="text-[#3A4557] text-xs mt-2">
            © 2025 Kavach Box by Prashant K Uunadkat | Shree Finserv
          </p>
        </div>
      </footer>
    </div>
  );
}
